// A single node in the Trie
class TrieNode {
  children: Map<string, TrieNode>;
  isEndOfWord: boolean;

  constructor() {
    this.children = new Map();
    this.isEndOfWord = false;
  }
}

// The Trie itself
export class Trie {
  private readonly root: TrieNode;

  constructor() {
    this.root = new TrieNode();
  }

  /**
   * Insert a word into the trie.
   * Example: trie.insert("cat")
   */
  insert(word: string): void {
    let node = this.root;

    for (const ch of word) {
      if (!node.children.has(ch)) {
        node.children.set(ch, new TrieNode());
      }
      node = node.children.get(ch)!; // "!" because we know it exists now
    }

    node.isEndOfWord = true;
  }

  construct(words: string[]): void {
    for (const word of words) {
      this.insert(word);
    }
  }

  /**
   * Check if a full word exists in the trie.
   * Example: trie.contains("cat") -> true/false
   */
  contains(word: string): boolean {
    let node = this.root;

    for (const ch of word) {
      const next = node.children.get(ch);
      if (!next) return false;
      node = next;
    }

    return node.isEndOfWord;
  }

  /**
   * Check if there is *any* word that starts with the given prefix.
   * Example: trie.startsWith("ca") -> true if "cat" or "car" etc exist.
   */
  startsWith(prefix: string): boolean {
    let node = this.root;

    for (const ch of prefix) {
      const next = node.children.get(ch);
      if (!next) return false;
      node = next;
    }

    return true;
  }

  /**
   * Get autocomplete suggestions for a given prefix.
   * Optionally limit how many results you want.
   *
   * Example:
   *   trie.insert("cat");
   *   trie.insert("car");
   *   trie.insert("dog");
   *   trie.autocomplete("ca") -> ["cat", "car"]
   */
  autocomplete(prefix: string, limit = Infinity): string[] {
    const results: string[] = [];

    // 1. Walk down the trie following the prefix
    let node = this.root;
    for (const ch of prefix) {
      const next = node.children.get(ch);
      if (!next) {
        return []; // no words with this prefix
      }
      node = next;
    }

    // 2. From this node, DFS to collect words
    const dfs = (currentNode: TrieNode, currentWord: string) => {
      if (results.length >= limit) return;

      if (currentNode.isEndOfWord) {
        results.push(currentWord);
      }

      for (const [ch, child] of currentNode.children) {
        dfs(child, currentWord + ch);
        if (results.length >= limit) return;
      }
    };

    dfs(node, prefix);
    return results;
  }
}
